import React, { useState } from 'react';
import { X, ShieldAlert, Check } from 'lucide-react';
import { safeFetch } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';
import { STAGE_ORDER, STAGE_LABELS, STAGE_GATES, CanonicalStage } from '../../../lib/workflow/transitions';
import type { StagePrerequisite } from '../../../lib/workflow/types';

interface OverrideModalProps {
  uid: string;
  applicantName?: string;
  currentStage: string;
  /** Structured blockers from the server contract; used to pre-select the gate. */
  blockers?: { message: string; owner: string; gateKey?: string }[];
  /** Current stage's prerequisites (server-computed). Only UNMET ones are offered as gates. */
  prerequisites?: StagePrerequisite[];
  onClose: () => void;
  onSuccess: () => void;
  /**
   * Optimistic display hooks (drawer responsiveness fix): fired the
   * moment the admin confirms, before the request resolves, so the
   * drawer shows the target stage immediately; `onMoveFailed` rolls
   * that display back when the server refuses.
   */
  onOptimisticMove?: (stage: string) => void;
  onMoveFailed?: () => void;
}

/**
 * Stage-incremental override dialog (refined spec 2026-10-11).
 *
 * - Target stage is FIXED to the next stage (spec §11: at most one stage per override).
 * - Gate options list ONLY the current stage's prerequisites — never other stages' gates.
 * - On confirm the applicant moves up automatically to the next stage.
 */
export const OverrideModal: React.FC<OverrideModalProps> = ({
  uid,
  applicantName,
  currentStage,
  blockers = [],
  prerequisites = [],
  onClose,
  onSuccess,
  onOptimisticMove,
  onMoveFailed,
}) => {
  // Target is always the next stage — not selectable.
  const targetStage: CanonicalStage = (() => {
    const idx = STAGE_ORDER.indexOf(currentStage as CanonicalStage);
    return idx >= 0 && idx < STAGE_ORDER.length - 1 ? STAGE_ORDER[idx + 1] : currentStage as CanonicalStage;
  })();

  // Stage-specific gates only (refined spec: never show other stages' gates).
  const stageGates = (STAGE_GATES as Record<string, { key: string; label: string }[]>)[currentStage] || [];
  const unmetKeys = new Set(prerequisites.filter((p) => !p.met).map((p) => p.key));
  // Offer unmet prerequisites first, then the remaining stage gates.
  const gateOptions = [
    ...stageGates.filter((g) => unmetKeys.has(g.key)),
    ...stageGates.filter((g) => !unmetKeys.has(g.key)),
  ];

  const [gateKeys, setGateKeys] = useState<string[]>(() => {
    // Gate integrity (2026-10-12): default-select EVERY unmet gate — each
    // unmet gate needs its own override + reason before the applicant can
    // advance, so the guided flow starts from full coverage.
    const unmet = gateOptions.filter((g) => unmetKeys.has(g.key)).map((g) => g.key);
    if (unmet.length > 0) return unmet;
    const fromBlocker = blockers.find((b) => b.gateKey)?.gateKey;
    if (fromBlocker && gateOptions.some((g) => g.key === fromBlocker)) return [fromBlocker];
    const first = gateOptions[0]?.key;
    return first ? [first] : [];
  });
  // Per-gate reasons (2026-10-12): every overridden gate carries its OWN
  // reason — one shared reason no longer covers multiple gates.
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const currentLabel = (STAGE_LABELS as any)[currentStage] || currentStage;
  const targetLabel = (STAGE_LABELS as any)[targetStage] || targetStage;
  const gateLabels = gateKeys.map((k) => gateOptions.find((g) => g.key === k)?.label || k);

  const toggleGate = (key: string) => {
    setGateKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const uncoveredUnmet = gateOptions.filter((g) => unmetKeys.has(g.key) && !gateKeys.includes(g.key));
  const selectedReasonsValid = gateKeys.length > 0
    && gateKeys.every((k) => (reasons[k] || '').trim().length >= 20);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (gateKeys.length === 0) {
      notifyToast('error', 'Select at least one gate being overridden.');
      return;
    }
    if (!selectedReasonsValid) {
      notifyToast('error', 'Every overridden gate needs its own reason of at least 20 characters.');
      return;
    }

    setSubmitting(true);
    // Confirmed: the drawer shows the target stage from this moment;
    // a refusal below rolls that display back.
    onOptimisticMove?.(targetStage);
    try {
      const res = await safeFetch(`/api/admin/workflow/applicants/${uid}/override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetStage,
          gateOverrides: gateKeys.map((k) => ({ gateKey: k, reason: (reasons[k] || '').trim() })),
          // Legacy mirrors for older consumers of the response/audit trail.
          gateKeys,
          reason: (reasons[gateKeys[0]] || '').trim(),
        }),
      });

      if (res?.success) {
        notifyToast('success', `Override applied: moved to ${targetLabel}`);
        onSuccess();
        onClose();
      } else {
        onMoveFailed?.();
      }
    } catch (err: any) {
      onMoveFailed?.();
      // 409 UNMET_GATES: some unmet gate was left uncovered — the server
      // names it; keep the modal open so the admin can complete coverage.
      notifyToast('error', err.message || 'Override action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9000] bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 animate-fade-in font-sans">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 space-y-4 overflow-hidden border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2 text-purple-900 font-extrabold text-sm">
            <ShieldAlert className="h-5 w-5 text-purple-600" />
            <span>Override Gate — {currentLabel}</span>
          </div>
          <button aria-label="Close" title="Close"
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Refined spec: the dialog states exactly what is bypassed. */}
        <div className="bg-purple-50 border border-purple-200 rounded-2xl p-3.5 text-xs text-purple-950 space-y-1.5">
          <p className="font-extrabold">
            Moving {applicantName ? <span className="underline">{applicantName}</span> : 'this applicant'} from{' '}
            <span className="font-black">{currentLabel}</span> to <span className="font-black">{targetLabel}</span>{' '}
            without:{' '}
            <span className="font-black">
              {gateLabels.length > 0 ? gateLabels.join(', ') : '—'}
            </span>.
          </p>
          <p className="text-[11px] font-medium text-purple-800">
            An override advances at most one stage and never fabricates an approval, invoice, or visa
            outcome. The true underlying state stays visible and the application carries a purple{' '}
            <span className="font-bold">Overridden</span> badge. This action is permanently logged.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Target stage (automatic — next stage only)</label>
            <div className="w-full bg-slate-100 border border-slate-200 rounded-xl p-2.5 text-xs font-bold text-slate-700">
              {targetLabel}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Gates being overridden * <span className="font-medium text-slate-400">({currentLabel} only — select all that apply)</span>
            </label>
            {gateOptions.length === 0 ? (
              <p className="text-xs text-slate-500 italic">No gates defined for this stage.</p>
            ) : (
              <div className="space-y-1.5">
                {gateOptions.map((g) => {
                  const isUnmet = unmetKeys.has(g.key);
                  const selected = gateKeys.includes(g.key);
                  return (
                    <button
                      key={g.key}
                      type="button"
                      onClick={() => toggleGate(g.key)}
                      aria-pressed={selected}
                      className={`w-full text-left rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${
                        selected
                          ? 'border-purple-500 bg-purple-50 text-purple-900'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-purple-300'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className={`inline-flex h-4 w-4 items-center justify-center rounded border-2 ${
                            selected ? 'border-purple-600 bg-purple-600' : 'border-slate-300 bg-white'
                          }`}
                        >
                          {selected && <Check className="h-3 w-3 text-white" />}
                        </span>
                        {g.label}
                        {isUnmet && (
                          <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                            unmet
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {gateKeys.length > 0 && (
              <p className="mt-1.5 text-[11px] font-bold text-purple-800">
                {gateKeys.length} gate{gateKeys.length === 1 ? '' : 's'} selected — each one needs its own reason below.
              </p>
            )}
            {uncoveredUnmet.length > 0 && (
              <p className="mt-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] font-bold text-amber-800">
                {uncoveredUnmet.length} unmet gate{uncoveredUnmet.length === 1 ? ' is' : 's are'} not selected
                ({uncoveredUnmet.map((g) => g.label).join(', ')}). Overriding one gate never waives another —
                the applicant cannot advance until every unmet gate is met or overridden.
              </p>
            )}
          </div>

          <div className="space-y-3">
            <label className="block text-xs font-bold text-slate-700">
              Override reason per gate * (min 20 chars each)
            </label>
            {gateKeys.length === 0 && (
              <p className="text-xs text-slate-500 italic">Select a gate above to give its override reason.</p>
            )}
            {gateKeys.map((key) => {
              const label = gateOptions.find((g) => g.key === key)?.label || key;
              const value = reasons[key] || '';
              const valid = value.trim().length >= 20;
              return (
                <div key={key}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-bold text-purple-900">{label}</span>
                    <span className={`text-[10px] font-mono font-bold ${valid ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {value.trim().length}/20
                    </span>
                  </div>
                  <textarea
                    rows={3}
                    value={value}
                    onChange={(e) => setReasons((prev) => ({ ...prev, [key]: e.target.value }))}
                    placeholder={`Why is "${label}" being bypassed for this applicant?`}
                    aria-label={`Override reason for ${label}`}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-medium text-slate-800 outline-none focus:bg-white focus:border-purple-500"
                  />
                </div>
              );
            })}
          </div>

          <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedReasonsValid}
              className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white font-extrabold rounded-xl shadow-xs cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              <span>{submitting ? 'Overriding...' : `Confirm Override → ${targetLabel}`}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
